import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, CardHeader, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SmartToy as BotIcon } from '@mui/icons-material';
import { ChatTranscript } from './ChatTranscript';
import { MessageComposer } from './MessageComposer';
import { sendAssistantChat } from '../../api/assistantChat';

const K = { navy: '#121826', blue: '#4185F1', border: 'rgba(18, 24, 38, 0.08)' };

const toApiPayload = (rows) =>
  (rows ?? []).map(({ role, content }) => ({ role, content }));

const toDisplayMessages = (rows) =>
  (rows ?? []).map((m, i) => ({
    id: `${i}-${m.at}`,
    type: m.role === 'assistant' ? 'agent' : 'customer',
    content: m.content,
    timestamp: m.at,
  }));

export const AssistantChatPanel = ({ sx: cardSx }) => {
  const [rows, setRows] = useState([]);
  const rowsRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const handleSend = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const at = new Date().toISOString();
    const userMsg = { role: 'user', content: trimmed, at };
    const nextRows = [...rowsRef.current, userMsg];

    setError(null);
    setLoading(true);
    setRows(nextRows);
    rowsRef.current = nextRows;

    try {
      const data = await sendAssistantChat(toApiPayload(nextRows));
      const reply = data?.reply ?? '';
      const replyAt = new Date().toISOString();
      setRows((prev) => [...prev, { role: 'assistant', content: reply, at: replyAt }]);
    } catch (e) {
      setError(e.message || 'Error al enviar');
      setRows((prev) => prev.slice(0, -1));
      rowsRef.current = rowsRef.current.slice(0, -1);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const displayMessages = toDisplayMessages(rows);

  return (
    <Card
      elevation={0}
      sx={[
        {
          border: `1px solid ${K.border}`,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'min(85vh, 640px)', sm: 'min(82vh, 720px)' },
          bgcolor: '#fff',
          overflow: 'hidden',
        },
        ...(cardSx ? [cardSx] : []),
      ]}
    >
      <CardHeader
        avatar={
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: alpha(K.blue, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BotIcon sx={{ color: K.blue, fontSize: 22 }} />
          </Box>
        }
        title={
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: K.navy }}>
            Asistente IA
          </Typography>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            Consultas internas sobre la plataforma y tus tareas
          </Typography>
        }
        sx={{ pb: 0, borderBottom: `1px solid ${K.border}` }}
      />
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ChatTranscript
            messages={displayMessages}
            emptyLabel="Escribe un mensaje para conversar con Kobra IA."
          />
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.5 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Pensando…
              </Typography>
            </Box>
          )}
          {error && (
            <Typography variant="caption" color="error" sx={{ px: 2, pb: 0.5 }}>
              {error}
            </Typography>
          )}
        </Box>
        <Box sx={{ p: 1.5, borderTop: `1px solid ${K.border}`, bgcolor: alpha(K.navy, 0.02) }}>
          <MessageComposer
            onSend={handleSend}
            disabled={loading}
            placeholder="Pregunta al asistente…"
          />
        </Box>
      </CardContent>
    </Card>
  );
};
